import { getObjectDiff } from "@azu-business/editor-v3";
import { omitDeep } from "functions/omitDeep";
import {
  ADD_DOMAIN_REPLACEMENT,
  ADD_SINGLE_REPLACEMENT,
  HierarchyContextProps,
  IHierarchyAction,
  LOAD_HIERARCHY,
  PATCH_HIERARCHY_COMMENTS,
  REMOVE_DOMAIN_REPLACEMENT,
  REMOVE_HIERARCHY_COMMENT,
  REMOVE_SINGLE_REPLACEMENT,
  SAVE_HIERARCHY_COMPLETED,
  SAVE_HIERARCHY_FAILED,
  SAVE_TOC_MAJOR_VERSION,
  UPDATE_DOMAIN_REPLACEMENT,
  UPDATE_HIERARCHY,
  UPDATE_HIERARCHY_COMMENT,
  UPDATE_SINGLE_REPLACEMENT,
  UPDATE_TLF_ITEM_INDEX,
} from "../interface";

/**
 * Reducer operations that should recompute the changed flag from hierarchy diffs.
 */
const changedOperations: Set<IHierarchyAction["operation"]> = new Set([
  ADD_DOMAIN_REPLACEMENT,
  ADD_SINGLE_REPLACEMENT,
  PATCH_HIERARCHY_COMMENTS,
  REMOVE_DOMAIN_REPLACEMENT,
  REMOVE_HIERARCHY_COMMENT,
  REMOVE_SINGLE_REPLACEMENT,
  UPDATE_DOMAIN_REPLACEMENT,
  UPDATE_HIERARCHY,
  UPDATE_HIERARCHY_COMMENT,
  UPDATE_SINGLE_REPLACEMENT,
  UPDATE_TLF_ITEM_INDEX,
]);

/**
 * Hierarchy fields excluded from changed-flag diffing because they are transient,
 * derived, or intentionally ignored by save-change detection.
 */
const changedIgnorePaths = [
  "comments",
  "tocMinorVersion",
  "sections",
  "children",
  "studyStandardsDate",
  "history",
];

/**
 * Recomputes the hierarchy changed flag for actions that mutate editable state.
 *
 * @param state - The updated hierarchy context state.
 * @param action - The action that produced the updated state.
 * @returns The updated hierarchy context state with the correct changed flag.
 */
export const hcr_updateChangedFlag = (
  state: HierarchyContextProps,
  action: IHierarchyAction,
): HierarchyContextProps => {
  if (
    action.operation === LOAD_HIERARCHY ||
    action.operation === SAVE_HIERARCHY_COMPLETED ||
    action.operation === SAVE_HIERARCHY_FAILED ||
    action.operation === SAVE_TOC_MAJOR_VERSION
  ) {
    return state;
  }

  if (!changedOperations.has(action.operation)) {
    return { ...state, changed: state.changed };
  }

  const changed = getObjectDiff(
    omitDeep(state.hierarchy, changedIgnorePaths),
    omitDeep(state.savedHierarchy, changedIgnorePaths),
  );

  return { ...state, changed: changed !== undefined };
};
